import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Splitter } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { parseFqnToPathParts } from '@/shared/lib/format';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import { DomainFormModal } from '@/features/domain';
import { DomainCategoryTree } from '@/features/domain/ui/DomainCategoryTree';

import { DomainDetailContent } from '@/widgets/domain';

import styles from './DomainPage.module.css';

const SPLITTER_SIZES = {
  DEFAULT: 300,
  MIN: 250,
  MAX: 500,
} as const;

export const DomainPage = () => {
  const { domainFqn: urlDomainFqn, tab } = useParams<{ domainFqn?: string; tab?: string }>();
  const navigate = useNavigate();

  const [breadcrumbPath, setBreadcrumbPath] = useState<string>(urlDomainFqn || '');
  const [selectedDomainFqn, setSelectedDomainFqn] = useState<string | null>(urlDomainFqn || null);
  const [currentTab, setCurrentTab] = useState<string>('sub-domains');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [refreshParentFqn, setRefreshParentFqn] = useState<string | null>(null);
  const [breadcrumbDisplayName, setBreadcrumbDisplayName] = useState<string>('');

  const handleAddClick = useCallback(() => {
    setIsFormModalOpen(true);
  }, []);

  const handleFormModalClose = useCallback(() => {
    setIsFormModalOpen(false);
  }, []);

  const handleFormSuccess = useCallback((parentFqn: string) => {
    setRefreshParentFqn(parentFqn);
  }, []);

  const handleDomainRefresh = useCallback((parentFqn: string) => {
    setRefreshParentFqn(parentFqn);
  }, []);

  const handleDomainDelete = useCallback((parentFqn: string) => {
    // navigate 전에 상태를 먼저 업데이트하여 타이밍 이슈 방지
    if (parentFqn) {
      setSelectedDomainFqn(parentFqn);
    } else {
      setSelectedDomainFqn(null);
    }
    setRefreshParentFqn(parentFqn);
  }, []);

  const handleRefreshComplete = useCallback(() => {
    setRefreshParentFqn(null);
  }, []);

  const handleDomainDetailLoaded = useCallback((displayName: string) => {
    setBreadcrumbDisplayName(displayName);
  }, []);

  const handleSetBreadcrumbPath = useCallback((fqn: string) => {
    setBreadcrumbPath(fqn);
  }, []);

  useEffect(() => {
    if (urlDomainFqn) {
      setSelectedDomainFqn(urlDomainFqn);
    } else {
      setSelectedDomainFqn(null);
    }
  }, [urlDomainFqn]);

  useEffect(() => {
    if (tab) {
      setCurrentTab(tab);
    } else if (selectedDomainFqn) {
      setCurrentTab('sub-domains');
      navigate(`/domain/${encodeURIComponent(selectedDomainFqn)}/sub-domains`, { replace: true });
    }
  }, [tab, selectedDomainFqn, navigate]);

  const breadcrumbItems = useMemo<BreadcrumbItemType[]>(() => {
    if (!selectedDomainFqn) return [];

    const items = [];

    const parts = parseFqnToPathParts(selectedDomainFqn, true);

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      const fqn = parts.slice(0, index + 1).join('.');

      if (!isLast) {
        items.push({
          title: (
            <Link
              to={`/domain/${encodeURIComponent(fqn)}`}
              onClick={() => handleSetBreadcrumbPath(fqn)}
              className={styles.breadcrumbItem}>
              <EllipsisText text={part} className={styles.breadcrumbText} />
            </Link>
          ),
        });
      }
    });

    items.push({
      title: (
        <span className={styles.breadcrumbItem}>
          <EllipsisText text={breadcrumbDisplayName || parts[parts.length - 1]} className={styles.breadcrumbText} />
        </span>
      ),
    });

    return items;
  }, [breadcrumbDisplayName, selectedDomainFqn, handleSetBreadcrumbPath]);

  return (
    <div className={styles.domainPage}>
      <Splitter className={styles.splitter}>
        <Splitter.Panel
          defaultSize={SPLITTER_SIZES.DEFAULT}
          min={SPLITTER_SIZES.MIN}
          max={SPLITTER_SIZES.MAX}
          resizable>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <DomainCategoryTree.Error
                {...props}
                title={NOTIFICATION_MESSAGES.DOMAIN_CATEGORY_ERROR}
                description={NOTIFICATION_MESSAGES.DOMAIN_CATEGORY_ERROR_DESC}
              />
            )}>
            <DomainCategoryTree.Tree
              selectedDomainFqn={selectedDomainFqn}
              breadcrumbPath={breadcrumbPath}
              handleSetBreadcrumbPath={handleSetBreadcrumbPath}
              onAddClick={handleAddClick}
              refreshParentFqn={refreshParentFqn}
              onRefreshComplete={handleRefreshComplete}
            />
          </ErrorBoundary>
        </Splitter.Panel>

        <Splitter.Panel>
          <ErrorBoundary
            FallbackComponent={(props) => (
              <DomainDetailContent.Error
                {...props}
                title={NOTIFICATION_MESSAGES.DOMAIN_DETAIL_ERROR}
                description={NOTIFICATION_MESSAGES.DOMAIN_DETAIL_ERROR_DESC}
              />
            )}>
            <Suspense fallback={<DomainDetailContent.Suspense />}>
              <div className={styles.mainContentArea}>
                <DomainDetailContent.Main
                  domainFqn={selectedDomainFqn}
                  currentTab={currentTab}
                  breadcrumbItems={breadcrumbItems}
                  onDomainRefresh={handleDomainRefresh}
                  onDomainDelete={handleDomainDelete}
                  onDomainDetailLoaded={handleDomainDetailLoaded}
                />
              </div>
            </Suspense>
          </ErrorBoundary>
        </Splitter.Panel>
      </Splitter>

      <DomainFormModal open={isFormModalOpen} onClose={handleFormModalClose} onSuccess={handleFormSuccess} />
    </div>
  );
};
